/** Climate classification: maps normalised height / moisture / temperature to a
 * terrain + biome + movement/build properties. Pure and deterministic. */
import { TerrainType, Biome, IMPASSABLE, DepositType } from '@settlers/shared';

export const SEA_LEVEL = 0.33;
const SHALLOW_LEVEL = 0.28;
const BEACH_LEVEL = 0.36;
const MOUNTAIN_LEVEL = 0.74;
const PEAK_LEVEL = 0.86;

export interface Classification {
  readonly terrain: TerrainType;
  readonly biome: Biome;
  readonly buildable: boolean;
  readonly moveCost: number;
  /** Deposit that may naturally spawn on this terrain (actual placement is
   * decided by the generator using additional noise + rng). */
  readonly depositCandidate: DepositType;
}

export const classify = (height: number, moisture: number, temperature: number): Classification => {
  // --- Water ---
  if (height < SHALLOW_LEVEL) {
    return {
      terrain: TerrainType.DeepWater,
      biome: Biome.Ocean,
      buildable: false,
      moveCost: IMPASSABLE,
      depositCandidate: DepositType.FishShoal,
    };
  }
  if (height < SEA_LEVEL) {
    return {
      terrain: TerrainType.ShallowWater,
      biome: Biome.Lake,
      buildable: false,
      moveCost: IMPASSABLE,
      depositCandidate: DepositType.FishShoal,
    };
  }
  if (height < BEACH_LEVEL) {
    return {
      terrain: TerrainType.Sand,
      biome: Biome.Beach,
      buildable: true,
      moveCost: 1.2,
      depositCandidate: DepositType.None,
    };
  }

  // --- High ground ---
  if (height >= PEAK_LEVEL) {
    return {
      terrain: temperature < 0.4 ? TerrainType.Snow : TerrainType.Rock,
      biome: temperature < 0.4 ? Biome.Snow : Biome.Mountain,
      buildable: false,
      moveCost: IMPASSABLE,
      depositCandidate: DepositType.GoldVein,
    };
  }
  if (height >= MOUNTAIN_LEVEL) {
    return {
      terrain: TerrainType.Rock,
      biome: Biome.Mountain,
      buildable: false,
      moveCost: 3.5,
      depositCandidate: temperature < 0.45 ? DepositType.CoalSeam : DepositType.IronVein,
    };
  }

  // --- Snow (cold uplands) ---
  if (temperature < 0.22) {
    return {
      terrain: TerrainType.Snow,
      biome: Biome.Snow,
      buildable: true,
      moveCost: 2,
      depositCandidate: DepositType.None,
    };
  }

  // --- Desert (hot & dry) ---
  if (temperature > 0.72 && moisture < 0.3) {
    return {
      terrain: TerrainType.Sand,
      biome: Biome.Desert,
      buildable: true,
      moveCost: 1.3,
      depositCandidate: DepositType.StoneRock,
    };
  }

  // --- Swamp (warm, very wet, low) ---
  if (moisture > 0.68 && height < 0.5) {
    return {
      terrain: TerrainType.Swamp,
      biome: Biome.Swamp,
      buildable: false,
      moveCost: 2.5,
      depositCandidate: DepositType.None,
    };
  }

  // --- Forest (wet) ---
  if (moisture > 0.5) {
    return {
      terrain: TerrainType.Forest,
      biome: Biome.Forest,
      buildable: false,
      moveCost: 1.6,
      depositCandidate: DepositType.Forest,
    };
  }

  // --- Meadow (default temperate) ---
  return {
    terrain: TerrainType.Grass,
    biome: Biome.Meadow,
    buildable: true,
    moveCost: 1,
    depositCandidate: moisture < 0.25 ? DepositType.StoneRock : DepositType.None,
  };
};
